export interface CellIssue {
  /** 1-based row number as shown to the user. */
  rowNumber: number;
  field: string;
  value: unknown;
  message: string;
}

export interface FieldIssueGroup {
  field: string;
  count: number;
  /** First message seen for the field, used as a representative example. */
  sampleMessage: string;
}

export interface ValidationSummaryResult {
  totalIssues: number;
  affectedRowCount: number;
  issuesByField: Array<FieldIssueGroup>;
  issues: Array<CellIssue>;
}

export type CellValidator = (
  key: string,
  value: unknown,
  row: Record<string, unknown>,
) => { isValid: boolean; errorMessage?: string };

/**
 * Aggregate per-cell validation into a centralized summary so studios see, in
 * one place, which fields/rows need attention before importing (QA 3.1/3.5/3.8).
 */
export function summarizeValidation(
  rows: Array<Record<string, unknown>>,
  validate: CellValidator,
): ValidationSummaryResult {
  const issues: Array<CellIssue> = [];
  const affectedRows = new Set<number>();
  const fieldGroups = new Map<string, FieldIssueGroup>();

  rows.forEach((row, index) => {
    const rowNumber = index + 1;
    for (const [field, value] of Object.entries(row)) {
      const result = validate(field, value, row);
      if (result.isValid) continue;

      const message = result.errorMessage ?? 'Invalid value.';
      issues.push({ rowNumber, field, value, message });
      affectedRows.add(rowNumber);

      const existing = fieldGroups.get(field);
      if (existing) {
        existing.count += 1;
      } else {
        fieldGroups.set(field, { field, count: 1, sampleMessage: message });
      }
    }
  });

  return {
    totalIssues: issues.length,
    affectedRowCount: affectedRows.size,
    issuesByField: [...fieldGroups.values()].sort((a, b) => b.count - a.count),
    issues,
  };
}
